"use client";
import React, { useMemo } from "react";
import {
  CarHistoryTimeline,
  CarHistoryEvent,
  SectionSubtitle,
  VehicleParameterItem,
} from "@/components";
import { AccidentItemHSPProps } from "@/components";
import { formatDate } from "@/helpers/helpers";

interface HistoryStatistics {
  totalOwners: number;
  averageOwnershipTime: string;
  mileageProgression: string;
  registrationChanges: number;
}

export interface RaportHSPHistoryData {
  timeline: CarHistoryEvent[];
  statistics: HistoryStatistics;
  accidents?: AccidentItemHSPProps[];
  registrationStatus?: string;
  technicalInspectionCurrent?: string;
  insuranceExpiryDate?: string;
  hasCurrentOCPolicy?: boolean;
  document?: {
    type: string;
    issuedDate: string;
    status: string;
  };
}

export interface RaportHSPTimelineProps {
  historyData: RaportHSPHistoryData;
}

export const HSPTimeline: React.FC<RaportHSPTimelineProps> = ({
  historyData,
}) => {
  const {
    timeline,
    registrationStatus,
    technicalInspectionCurrent,
    insuranceExpiryDate,
    hasCurrentOCPolicy,
    document,
  } = historyData;
  const fillColor = useMemo(() => "#A4B2C0", []);

  const hasAdditionalInfo =
    registrationStatus ||
    technicalInspectionCurrent ||
    insuranceExpiryDate ||
    hasCurrentOCPolicy !== undefined ||
    document;

  return (
    <div className="overflow-hidden mt-6 lg:mt-11">
      {hasAdditionalInfo && (
        <div className="mb-10">
          <SectionSubtitle
            title="Identyfikacja pojazdu"
          />
          <div className="grid grid-cols-1 gap-5 py-5 mt-10 mb-6 bg-white rounded-2xl border border-violet-100 border-solid md:grid-cols-2 lg:grid-cols-3">
            {registrationStatus && (
              <VehicleParameterItem
                label="Status rejestracji"
                value={registrationStatus}
                fillColor={fillColor}
                icon="file"
              />
            )}

            {technicalInspectionCurrent && (
              <VehicleParameterItem
                label="Badanie techniczne"
                value={technicalInspectionCurrent}
                fillColor={fillColor}
                icon="doc-ok"
              />
            )}

            {/* {insuranceExpiryDate && (
              <VehicleParameterItem
                label={t(
                  "timeline.insurance_expiry_date",
                  "Data wygaśnięcia ubezpieczenia"
                )}
                value={formatDate(insuranceExpiryDate) || insuranceExpiryDate}
                fillColor={fillColor}
                icon="calendar-date"
              />
            )} */}

            {hasCurrentOCPolicy !== undefined && (
              <VehicleParameterItem
                label="Polisa OC"
                value={
                  hasCurrentOCPolicy
                    ? "Aktualna"
                    : "Brak"
                }
                icon="car-shield"
              />
            )}

            {document && (
              <>
                {document.type && (
                  <VehicleParameterItem
                    label="Typ dokumentu"
                    value={document.type}
                    icon="car-card"
                  />
                )}
                {document.issuedDate && (
                  <VehicleParameterItem
                    label="Data wydania dokumentu"
                    value={
                      formatDate(document.issuedDate) || document.issuedDate
                    }
                    fillColor={fillColor}
                    icon="calendar-date"
                  />
                )}
                {document.status && (
                  <VehicleParameterItem
                    label="Status dokumentu"
                    value={document.status}
                    fillColor={fillColor}
                    icon="hand-giving"
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      <SectionSubtitle title="Oś czasu" />

      <div className="pl-4 md:pl-0">
        <CarHistoryTimeline
          events={timeline}
          className="mt-6"
          i18n={{
            ownerLabel: "Właściciel:",
            ownerTypeIndividual: "osoba fizyczna",
            ownerTypeCompany: "osoba prawna",
            voivodeshipLabel: "Województwo, w którym pojazd zarejestrowano:",
            previousOwnerDateTemplate: "(pozostał właścicielem do {date})",
            inspectionResultLabel: "Wynik badania:",
            inspectionResultPositive: "pozytywny",
            inspectionResultNegative: "negatywny",
            inspectionNumberLabel: "Numer SKP:",
            mileageLabel: "Odczytany stan drogomierza:",
            kmUnit: "km",
          }}
        />
      </div>
    </div>
  );
};
